// Binding layer between the central portfolio slice and the presentational
// Portfolio screen. The page consumes `{ data, loading, error, refetch }`, so the
// page + reusable components stay presentational — this hook just sources that
// from Redux.
//
// Orchestration: identity is owned by the app-global auth slice (bootstrapped once
// by <AuthProvider>; <RequireAuth> gates the page until authenticated). This hook
// reads the selected account from auth and (re)fetches /portfolio whenever that id
// changes. The multi-plan switcher only dispatches setSelectedAccount(id); this
// refetches automatically — no change needed here.
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/AuthProvider';
import { selectBenchmark } from './portfolio.benchmark';
import {
  fetchPortfolio,
  selectPortfolioData,
  selectPortfolioStatus,
  selectPortfolioError,
} from '../../../store/slices/portfolio.slice';

// Stable empty shape for the not-yet-loaded window: the slice no longer seeds a
// mock plan, so `data` is null until the first fetch resolves. The page reads
// `data.funds || []` etc. during its loading render, so it gets an object —
// stable, so memo deps don't churn.
const EMPTY = {};

export function usePortfolioData() {
  const dispatch = useDispatch();
  const { selectedAccountId: accountId, status: authStatus, error: authError } = useAuth();

  const data = useSelector(selectPortfolioData);
  const status = useSelector(selectPortfolioStatus);
  const dataError = useSelector(selectPortfolioError);

  // Identity + a selected account are required before there's anything to fetch —
  // the mock path is gone; /portfolio is the only source.
  const shouldFetch = authStatus === 'authenticated' && !!accountId;

  // (Re)fetch once identity is ready and whenever the selected account changes.
  useEffect(() => {
    if (shouldFetch) dispatch(fetchPortfolio(accountId));
  }, [shouldFetch, accountId, dispatch]);

  const refetch = useCallback(() => {
    if (shouldFetch) dispatch(fetchPortfolio(accountId));
  }, [shouldFetch, accountId, dispatch]);

  // Loading covers: identity in flight, data in flight, or selection resolved but
  // its fetch hasn't started yet (avoids an empty-state flash before dispatch).
  const loading =
    authStatus === 'loading' || (shouldFetch && (status === 'idle' || status === 'loading'));

  const error = authError || dataError || null;

  return { data: data || EMPTY, loading, error, refetch };
}

/**
 * The finished benchmark for one range (FM-2101), localized. Memoized on data +
 * range, so a plan switch re-runs the selector and every card reading it re-renders.
 *
 * @param {object} data the portfolio UI model
 * @param {string} range compare range key ('YTD', '1Y', …)
 */
export function useBenchmark(data, range) {
  const { t } = useTranslation();
  return useMemo(() => selectBenchmark(data, range, { t }), [data, range, t]);
}

// `useCompareBenchmark` lived here: the range-picking binding for the
// portfolio-vs-benchmark card. That section was removed from the page, and the binding
// existed for nothing else, so it went with it. `availableRanges` (portfolio.benchmark)
// still answers which ranges a plan carries, for whatever asks next.

export default usePortfolioData;
